import { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatIDR } from "@/components/AppTopBar";

export function WithdrawalPanel({ accounts: sharedAccounts = [], mappings: sharedMappings = [], fundRows = [], onDone }) {
  const [accounts, setAccounts] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [src, setSrc] = useState("");
  const [dst, setDst] = useState("");
  const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };
  const [actualDate, setActualDate] = useState(todayLocal);
  const [modal, setModal] = useState("");
  const [profit, setProfit] = useState("");
  const [uncl, setUncl] = useState("");
  const [avail, setAvail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  useEffect(() => {
    setAccounts(Array.isArray(sharedAccounts) ? sharedAccounts : []);
    setMappings(Array.isArray(sharedMappings) ? sharedMappings : []);
  }, [sharedAccounts, sharedMappings]);

  const allWallets = accounts.filter((a) => a.account_type === "MARKETPLACE_WALLET" && String(a.is_active) === "true");
  const banks = accounts.filter((a) => a.account_type === "BANK" && String(a.is_active) === "true");
  const mappingByAccount = useMemo(() => {
    const map = new Map();
    mappings.filter((m) => m.is_active !== false && m.account_id).forEach((m) => {
      if (!map.has(m.account_id)) map.set(m.account_id, []);
      map.get(m.account_id).push(m);
    });
    return map;
  }, [mappings]);

  // Withdrawal form may only execute against a wallet with an active mapping.
  // Unmapped marketplace wallets remain in master data; they are simply not selectable here.
  const wallets = allWallets.filter((a) => (mappingByAccount.get(a.account_id) || []).length > 0);

  const walletLabel = (a) => {
    const ms = mappingByAccount.get(a.account_id) || [];
    if (!ms.length) return `⚠ BELUM DIPETAKAN — ${a.account_name}`;
    const identities = [...new Set(ms.map((m) => `${m.store_name || m.store_name_source || "Toko"} · ${m.marketplace || "MARKETPLACE"}`))];
    return `${identities.join(" / ")} — ${a.account_name}`;
  };

  useEffect(() => {
    if (!src) { setAvail(null); return; }
    const row = (Array.isArray(fundRows) ? fundRows : []).find((r) => r.account_id === src);
    setAvail(row || null);
  }, [src, fundRows]);

  const n = (v) => parseFloat(v) || 0;
  const total = n(modal) + n(profit) + n(uncl);

  const resetPreview = () => { setPreview(null); setKey(""); setOk(""); };
  const onSel = (setter) => (e) => { setter(e.target.value); resetPreview(); setErr(""); };
  const onNum = (setter) => (e) => { setter(e.target.value); resetPreview(); setErr(""); };

  const doPreview = () => {
    setErr(""); setOk(""); setPreview(null);
    if (!src) return setErr("Wallet belum dipilih");
    if (!dst) return setErr("Bank belum dipilih");
    if (!actualDate || actualDate > todayLocal()) return setErr("Tanggal penarikan tidak valid");
    if (total <= 0) return setErr("Total penarikan harus > 0");
    setBusy(true);
    const q = `source_account_id=${src}&dest_account_id=${dst}&modal_amount=${n(modal)}&profit_amount=${n(profit)}&unclassified_amount=${n(uncl)}`;
    api.get(`/marketplace/withdrawal/preview?${q}`)
      .then(({ data }) => { setPreview(data); setKey(crypto.randomUUID()); })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Preview gagal"))
      .finally(() => setBusy(false));
  };

  const doConfirm = () => {
    if (!preview || !key || busy) return;
    setBusy(true); setErr("");
    api.post("/marketplace/withdrawal", {
      withdrawal_key: key, source_account_id: src, dest_account_id: dst,
      actual_withdrawal_date: actualDate,
      modal_amount: n(modal), profit_amount: n(profit), unclassified_amount: n(uncl),
    })
      .then(({ data }) => {
        setOk(`Penarikan berhasil. Ref: ${data.withdrawal_key || data.id}`);
        setSrc(""); setDst(""); setActualDate(todayLocal()); setModal(""); setProfit(""); setUncl(""); setPreview(null); setKey(""); setAvail(null);
        if (onDone) onDone();
      })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Penarikan gagal"))
      .finally(() => setBusy(false));
  };

  const Row = ({ label, value, strong }) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontWeight: strong ? 700 : 500 }}>
      <span style={{ color: "var(--muted)" }}>{label}</span><span className="mono">{value}</span>
    </div>
  );

  const selectedWallet = wallets.find((a) => a.account_id === src);

  return (
    <div data-testid="withdrawal-panel" className="panel" style={{ width: "100%", maxWidth: "none", boxSizing: "border-box", marginBottom: 18, padding: 28 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, paddingBottom: 18, borderBottom: "1px solid var(--border)", marginBottom: 24 }}>
        <div style={{ width: 52, height: 52, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: "#dff6f0", color: "#0f766e", fontSize: 28, flexShrink: 0 }}>▣</div>
        <div>
          <h2 style={{ margin: 0, fontSize: 24, lineHeight: 1.2 }}>Penarikan Wallet → Bank</h2>
          <div className="hint" style={{ marginTop: 5, fontSize: 14 }}>Pilih sumber wallet dan bank tujuan, lalu tentukan kategori dana yang ditarik.</div>
        </div>
      </div>
      {err && <div className="alert-error" data-testid="withdrawal-error">{err}</div>}
      {ok && <div className="alert-ok" data-testid="withdrawal-success">{ok}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 22, alignItems: "start", paddingBottom: 22, borderBottom: "1px solid var(--border)" }}>
        <div>
          <label className="field-label" style={{ fontSize: 14, fontWeight: 700 }}>DARI WALLET / TOKO</label>
          <select className="field-input" value={src} onChange={onSel(setSrc)} data-testid="withdrawal-source">
            <option value="">— Pilih Toko · Marketplace · Wallet —</option>
            {wallets.map((a) => <option key={a.account_id} value={a.account_id}>{walletLabel(a)}</option>)}
          </select>
          {selectedWallet && <div className="hint" style={{ marginTop: 4 }}>Sumber: <b>{walletLabel(selectedWallet)}</b></div>}
        </div>
        <div>
          <label className="field-label" style={{ fontSize: 14, fontWeight: 700 }}>KE BANK</label>
          <select className="field-input" value={dst} onChange={onSel(setDst)} data-testid="withdrawal-dest">
            <option value="">— Pilih Bank —</option>
            {banks.map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label" style={{ fontSize: 14, fontWeight: 700 }}>TANGGAL PENARIKAN</label>
          <input type="date" className="field-input" value={actualDate} max={todayLocal()} onChange={onSel(setActualDate)} data-testid="withdrawal-actual-date" />
          <div className="hint" style={{ marginTop: 4 }}>Tanggal aktual saja; jurnal tetap tanggal posting.</div>
        </div>
      </div>
      {avail && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, marginTop: 16 }}>
          <div className="dash-fund-item modal" style={{ padding: "9px 12px" }}><span>MODAL tersedia</span><b>{formatIDR(avail.modal_available)}</b></div>
          <div className="dash-fund-item profit" style={{ padding: "9px 12px" }}><span>PROFIT tersedia</span><b>{formatIDR(avail.profit_available)}</b></div>
          <div className="dash-fund-item uncl" style={{ padding: "9px 12px" }}><span>UNCLASSIFIED tersedia</span><b>{formatIDR(avail.unclassified_available)}</b></div>
        </div>
      )}
      <div style={{ marginTop: avail ? 20 : 16, paddingTop: 18, borderTop: "1px solid var(--border)" }}>
        <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: ".01em", marginBottom: 16 }}>KATEGORI DANA YANG DITARIK</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 10, alignItems: "end" }}>
          <div><label className="field-label" style={{ fontSize: 14, fontWeight: 700 }}>MODAL</label><input type="number" min="0" className="field-input" value={modal} onChange={onNum(setModal)} placeholder="0" data-testid="withdrawal-modal" /></div>
          <div><label className="field-label" style={{ fontSize: 14, fontWeight: 700 }}>PROFIT</label><input type="number" min="0" className="field-input" value={profit} onChange={onNum(setProfit)} placeholder="0" data-testid="withdrawal-profit" /></div>
          <div><label className="field-label" style={{ fontSize: 14, fontWeight: 700 }}>UNCLASSIFIED</label><input type="number" min="0" className="field-input" value={uncl} onChange={onNum(setUncl)} placeholder="0" data-testid="withdrawal-uncl" /></div>
          <div><label className="field-label" style={{ fontSize: 14, fontWeight: 700 }}>TOTAL PENARIKAN</label><input className="field-input" value={formatIDR(total)} readOnly data-testid="withdrawal-total" /></div>
          <button className="btn-primary" disabled={busy} onClick={doPreview} data-testid="withdrawal-preview-btn" style={{ width: "100%", minHeight: 52, fontSize: 15 }}>{busy && !preview ? "Menghitung…" : "Preview Komposisi"}</button>
        </div>
      </div>
      {preview && (
        <div style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 12 }} data-testid="withdrawal-preview">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div>
              <div style={{ fontWeight: 800, marginBottom: 5 }}>Saldo Setelah Penarikan</div>
              <Row label="Saldo Fisik Saat Ini" value={formatIDR(preview.wallet_physical)} />
              <Row label="MODAL Saat Ini" value={formatIDR(preview.wallet_modal)} />
              <Row label="PROFIT Saat Ini" value={formatIDR(preview.wallet_profit)} />
              <Row label="UNCLASSIFIED Saat Ini" value={formatIDR(preview.wallet_unclassified)} />
              <Row label="Saldo Wallet Setelah" value={formatIDR(preview.wallet_after)} strong />
            </div>
            <div>
              <div style={{ fontWeight: 800, marginBottom: 5 }}>Komposisi Dipindahkan</div>
              <Row label="MODAL" value={<span data-testid="prev-alloc-modal">{formatIDR(preview.alloc_modal)}</span>} />
              <Row label="PROFIT" value={<span data-testid="prev-alloc-profit">{formatIDR(preview.alloc_profit)}</span>} />
              <Row label="UNCLASSIFIED" value={<span data-testid="prev-alloc-uncl">{formatIDR(preview.alloc_unclassified)}</span>} />
              <Row label="TOTAL" value={<span data-testid="prev-alloc-total">{formatIDR(preview.amount)}</span>} strong />
              <Row label={"Saldo Bank Setelah (" + preview.bank_name + ")"} value={formatIDR(preview.bank_after)} strong />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <button className="btn-primary" disabled={busy} onClick={doConfirm} data-testid="withdrawal-confirm-btn" style={{ padding: "9px 18px" }}>{busy ? "Memproses…" : "Konfirmasi Penarikan"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
